<div class="title-bar title-bar--responsive">
  <div class="title-bar__heading">
    <h1>BRANDING</h1>
    <app-help screen="onboarding"></app-help>
  </div>
</div>

<div class="status-message" aria-live="polite" role="status">
  @if (statusMessage) {
    <span>{{ statusMessage }}</span>
  }
</div>
@if (errorMessage) {
  <div class="error-message" role="alert">
    {{ errorMessage }}
  </div>
}

<div class="action-panel" role="region" aria-label="Draft actions" [attr.aria-busy]="isApplyingDraft || isResettingDraft ? 'true' : null">
  @if (ups.draftChanges | async; as hasChanges) {
    <button mat-flat-button color="primary" type="button" (click)="makeChangesLive()" [disabled]="isApplyingDraft || !hasChanges">
      @if (isApplyingDraft) {
        <span>Making Live…</span>
      }
      @if (!isApplyingDraft) {
        <span>Make Changes Live</span>
      }
    </button>
    <button mat-stroked-button color="warn" type="button" (click)="resetDraftToLive()" [disabled]="isResettingDraft || !hasChanges">
      @if (isResettingDraft) {
        <span>Restoring Draft…</span>
      }
      @if (!isResettingDraft) {
        <span>Discard Draft Changes</span>
      }
    </button>
    @if (!hasChanges && !isApplyingDraft && !isResettingDraft) {
      <span class="no-changes"> No draft changes detected. </span>
    }
  }
</div>

<mat-vertical-stepper class="onboarding-stepper" #onboardingStepper (selectionChange)="onStepperSelectionChange($event)" [disableRipple]="true">
  <mat-step [completed]="!schoolNameForm.dirty" [editable]="savingStep === null && uploadingStep === null">
    <ng-template matStepLabel>School Name</ng-template>
    <form [formGroup]="schoolNameForm" class="step-form" aria-labelledby="general-step">
      <!-- <h2 #stepHeading id="general-step" tabindex="-1">General Settings</h2>
      <p class="step-description">Update the core information shown to students inside the calculator.</p> -->
      <mat-form-field appearance="outline" class="wide-field">
        <mat-label>University Name</mat-label>
        <input matInput formControlName="universityName" required autocomplete="organization" [disabled]="savingStep === 'school-name'" />
        @if (schoolNameForm.controls["universityName"].hasError("required")) {
          <mat-error> University name is required. </mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="wide-field">
        <mat-label>School Abbreviation</mat-label>
        <input matInput formControlName="shortName" required maxlength="20" autocomplete="organization" [disabled]="savingStep === 'school-name'" />
        <mat-hint align="start">Used in navigation labels.</mat-hint>
        <mat-hint align="end">{{ schoolNameForm.controls["shortName"].value?.length || 0 }} / 20</mat-hint>
        @if (schoolNameForm.controls["shortName"].hasError("required")) {
          <mat-error> School Abbreviation is required. </mat-error>
        }
      </mat-form-field>

      <!-- <mat-form-field appearance="outline" class="wide-field">
      <mat-label>Exit URL</mat-label>
      <input matInput formControlName="npcExitUrl" autocomplete="url" [disabled]="savingStep === 'school-name'" />
    </mat-form-field> -->

      <!-- <mat-checkbox formControlName="showEFC" [disabled]="savingStep === 'school-name'"> Show SAI or Family Contribution on results </mat-checkbox> -->

      <div class="nav-buttons">
        <!-- <span></span> -->
        <button mat-flat-button color="primary" type="button" (click)="goToNext()" [disabled]="savingStep === 'school-name'">Next</button>
      </div>
    </form>
  </mat-step>

  <mat-step [completed]="!brandingForm.dirty && uploadingStep === null" [editable]="savingStep === null && uploadingStep === null">
    <ng-template matStepLabel>Logo Banner</ng-template>
    <form [formGroup]="brandingForm" class="step-form" aria-labelledby="branding-step">
      @if (!headerColorLocked) {
        <section class="branding-section">
          <header class="branding-section__header">
            <h3 id="logo-step">School Logo</h3>
            <p class="branding-section__summary">Appears in the global header beside your navigation.</p>
          </header>
          <div class="asset-column">
            <img class="logo-preview" (load)="storeImageSize($event, 'logo')" [style.background-color]="headerColorValue || '#ffffff'" [src]="(ups.logoURL | async) || ups.loadingLogo" alt="School logo preview" />
            @if (logoSize | async; as size) {
              <span class="image-meta">
                Current logo size: {{ size.width }} × {{ size.height }} px
                @if (size.height > 80) {
                  <span class="image-meta-warning"> - Recommended height: 80 px or less.</span>
                }
              </span>
            }
            <div class="button-column">
              <button mat-raised-button color="primary" type="button" (click)="logoUpload.click()" [disabled]="uploadingStep !== null || savingStep === 'logo-banner'">
                {{ uploadingStep === "logo" ? "Uploading…" : "Upload Logo" }}
              </button>
              <input #logoUpload type="file" accept="image/*" (change)="uploadFile($event, 'logo')" class="file-input" aria-label="Upload school logo" />
              @if (!(ups.logoURL | async)?.includes("/assets/")) {
                <button mat-stroked-button color="warn" type="button" (click)="removeImageRef('logo')" [disabled]="uploadingStep !== null || savingStep === 'logo-banner'">Remove Logo</button>
              }
            </div>
          </div>
        </section>
      }
      <section class="branding-section header-preview-card">
        <header class="branding-section__header">
          <h3>Header Background</h3>
          <p class="branding-section__summary">Set the color of the header background.</p>
        </header>
        @if (headerColorLocked) {
          <div class="header-guidance header-warning" role="note">
            <strong class="header-warning__title">Custom header styles in use</strong>
            <p>
              This NPC uses custom header styling. Please reach out to support at
              <a href="mailto:npc_support@rewiretech.com">npc_support&#64;rewiretech.com</a>
              if you need to change the header background.
            </p>
          </div>
        }
        @if (!headerColorLocked) {
          <p class="header-guidance">Enter a HEX value or use the color picker to align the header background with your brand palette.</p>
          <div class="header-inputs">
            <mat-form-field appearance="outline" class="header-color-field">
              <mat-label>Header Color</mat-label>
              <input matInput type="text" formControlName="headerColor" aria-describedby="header-color-help" [disabled]="savingStep === 'logo-banner'" />
              <mat-hint id="header-color-help">Example: #0b2a72</mat-hint>
              @if (brandingForm.controls["headerColor"].hasError("required")) {
                <mat-error> Enter a header color before continuing. </mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline" class="header-color-swatch">
              <mat-label>Header Color Picker</mat-label>
              <input matInput type="color" formControlName="headerColor" aria-haspopup="dialog" role="button" [disabled]="savingStep === 'logo-banner'" />
            </mat-form-field>
          </div>
        }
      </section>
    </form>
    <div class="nav-buttons">
      <button mat-raised-button type="button" (click)="goToPrevious()" [disabled]="!canGoBack || savingStep === 'logo-banner' || uploadingStep !== null">Back</button>
      <button mat-raised-button type="button" (click)="goToNext()" [disabled]="savingStep === 'logo-banner' || uploadingStep !== null">Next</button>
    </div>
  </mat-step>
  <mat-step [completed]="uploadingStep !== 'welcome'" [editable]="savingStep === null && uploadingStep === null">
    <ng-template matStepLabel>Welcome Page Image</ng-template>
    <div class="step-form" aria-labelledby="branding-step">
      <section class="branding-section">
        <div class="asset-column">
          <img class="welcome-preview" (load)="storeImageSize($event, 'welcome')" [src]="(ups.welcomeImageURL | async) || ups.loadingLogo" alt="Welcome page image preview" />
          @if (welcomeSize | async; as size) {
            <span class="image-meta">
              Current image size: {{ size.width }} × {{ size.height }} px
              @if (size.height > 300) {
                <span class="image-meta-warning"> - Recommended height: 300 px or less.</span>
              }
            </span>
          }
          <div class="button-column">
            <button mat-raised-button color="primary" type="button" (click)="welcomeUpload.click()" [disabled]="uploadingStep !== null || savingStep === 'welcome-page'">
              {{ uploadingStep === "welcome" ? "Uploading…" : "Upload Welcome Image" }}
            </button>
            <input #welcomeUpload type="file" accept="image/*" (change)="uploadFile($event, 'welcome')" class="file-input" aria-label="Upload welcome page image" />
            @if (!(ups.welcomeImageURL | async)?.includes("/assets/")) {
              <button mat-stroked-button color="warn" type="button" (click)="removeImageRef('welcome')" [disabled]="uploadingStep !== null || savingStep === 'welcome-page'">Remove Welcome Image</button>
            }
          </div>
        </div>
      </section>
      <!-- </div> -->
      <div class="nav-buttons">
        <button mat-raised-button type="button" (click)="goToPrevious()" [disabled]="!canGoBack || savingStep === 'welcome-page' || uploadingStep !== null">Back</button>
        <button mat-raised-button type="button" (click)="goToNext()" [disabled]="savingStep === 'welcome-page' || uploadingStep !== null">Next</button>
      </div>
    </div>
  </mat-step>
  <mat-step [completed]="!cohortForm.dirty">
    <ng-template matStepLabel>Mandatory Cohort Phrase</ng-template>
    <form [formGroup]="cohortForm" class="step-form" aria-labelledby="cohort-section-title">
      <section class="cohort-section">
        <!-- <h3 id="cohort-section-title">Cohort Phrase</h3> -->
        <p class="cohort-summary">Percent of the cohort (full-time, first-time students) that received grant aid</p>
        <mat-form-field appearance="outline" class="cohort-field">
          <mat-label>Aid Year</mat-label>
          <mat-select formControlName="cohortAidPercentYear" [disabled]="savingStep === 'cohort-phrase'">
            @for (year of cohortYears; track year) {
              <mat-option [value]="year">{{ year }}</mat-option>
            }
            @if (legacyCohortYear; as year) {
              <mat-option [value]="year" disabled>{{ year }}(x)</mat-option>
            }
          </mat-select>
          @if (cohortForm.controls["cohortAidPercentYear"].hasError("required")) {
            <mat-error> Select an aid year. </mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="cohort-field">
          <mat-label>Aid Percent</mat-label>
          <input matInput type="number" formControlName="cohortAidPercent" [disabled]="savingStep === 'cohort-phrase'" />
          <span matSuffix>%</span>
          @if (cohortForm.controls["cohortAidPercent"].hasError("required")) {
            <mat-error> Add a percent. </mat-error>
          }
          @if (cohortForm.controls["cohortAidPercent"].hasError("min") || cohortForm.controls["cohortAidPercent"].hasError("max")) {
            <mat-error> Enter a value between 0 and 100. </mat-error>
          }
        </mat-form-field>
      </section>
      <div class="nav-buttons">
        <button mat-raised-button type="button" (click)="goToPrevious()" [disabled]="!canGoBack || savingStep === 'cohort-phrase'">Back</button>
        <button mat-flat-button color="primary" type="button" (click)="saveCohortStep()" [disabled]="cohortForm.invalid || !cohortForm.dirty || savingStep === 'cohort-phrase'">
          {{ savingStep === "cohort-phrase" ? "Saving…" : cohortForm.dirty ? "Save Changes" : "No unsaved changes" }}
        </button>
      </div>
    </form>
  </mat-step>
</mat-vertical-stepper>
